import { type StoreName, type SkuId, type LengthInSeconds } from '@bluebrick/discounts-service-module'
import { HttpStatusCode, isAxiosError } from 'axios'
import { DateTime } from 'luxon'
import { z } from 'zod'
import { useCache } from '@/common/composables/useCache'
import { toISODateStringUTC } from '@/common/dateUtils'
import { stripName } from '@/common/utils'
import { useApi } from '@/features/api/useAPI'
import { useLoadersStore } from '@/features/loaders/useLoadersStore'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { discountConfigSchema, historyEventsListSchema, recommendedDatesMapSchema } from '@/schema'
import { type HistoryEvent, type UpsertEventRequestBody, type EventData, type RecommendedDates, type DiscountConfig } from '@/types'

export async function fetchRecommendedDiscountDepth(
	storeName: StoreName,
	startDate: DateTime,
	endDate: DateTime
): Promise<Record<string, number> | undefined> {
	const { activate, deactivate } = useLoadersStore()
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		activate('maxDiscountDepth')

		const { data: discountDepth } = await apiPost<Record<SkuId, number>>(
			`/events/v2/store/${storeName}/discountDepth`,
			{
				startDate: toISODateStringUTC(startDate),
				endDate: toISODateStringUTC(endDate)
			},
			{ fetchOptions: { canRetry: true } }
		)

		return discountDepth
	} catch (e) {
		trackException(e as Error, 'Failed to load discount recommendations.', {
			storeName,
			startDate: toISODateStringUTC(startDate) ?? undefined,
			endDate: toISODateStringUTC(endDate) ?? undefined
		})

		notifyError('Failed to load discount recommendations.')
	} finally {
		deactivate('maxDiscountDepth')
	}
}

export async function fetchDurationsForStoreSkus(
	storeName: StoreName,
	startDate: DateTime,
	skuIds: SkuId[],
	events: EventData[],
	cooldowns: EventData[]
): Promise<Record<SkuId, { maxLength: LengthInSeconds; optimalLength: LengthInSeconds }> | undefined> {
	const { activate, deactivate } = useLoadersStore()
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		activate('maxDiscountLength')

		const { data: maxAndOptimalDurations } = await apiPost<Record<SkuId, { maxLength: LengthInSeconds; optimalLength: LengthInSeconds }>>(
			`/events/v2/store/${storeName}/maxAndOptimalEventLengths?startDate=${toISODateStringUTC(startDate)}`,
			{
				startDate,
				skuIds,
				// #event-names-vs-firewall
				events: events.map(stripName),
				cooldowns: cooldowns.map(stripName)
			},
			{ fetchOptions: { canRetry: true } }
		)

		return Object.entries(maxAndOptimalDurations).reduce<Record<SkuId, { maxLength: LengthInSeconds; optimalLength: LengthInSeconds }> | undefined>(
			(result, [skuId, { maxLength, optimalLength }]) => ({
				...result,
				[skuId]: {
					maxLength: maxLength / (60 * 60 * 24),
					optimalLength: optimalLength / (60 * 60 * 24)
				}
			}),
			{}
		)
	} catch (e) {
		trackException(e as Error, 'Failed to load max duration of events.', {
			storeName,
			startDate: toISODateStringUTC(startDate) ?? undefined
		})

		// TODO: This `if` can be removed once DA-170 is integrated (the `notifyError`
		// will stay)
		// Currently ES API returns a 422 error if the `POST /max_event_length`
		// endpoint does not receive any events or cooldowns as a payload.
		// It can happen if we ask for max length around a date where there is
		// no events present (before or after).
		// Once DA-170 is integrated this will be handled on API side and no
		// additional checks will be required here
		if (!isAxiosError(e) || e.response?.status !== HttpStatusCode.UnprocessableEntity) {
			notifyError('Failed to load max duration of events.')
		}
	} finally {
		deactivate('maxDiscountLength')
	}
}

export async function fetchRecommendedDates(
	storeName: StoreName,
	startDate: DateTime,
	endDate: DateTime,
	minimalDate: DateTime,
	events: EventData[]
) {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { activate, deactivate } = useLoadersStore()
	const { apiPost } = useApi()

	try {
		activate('recommendedDates')

		const { data } = await apiPost<Record<SkuId, RecommendedDates>>(
			`/events/store/${storeName}/recommendedDates`,
			{
				startDate: toISODateStringUTC(startDate),
				endDate: toISODateStringUTC(endDate),
				minimalDate: toISODateStringUTC(minimalDate),
				// #event-names-vs-firewall
				events: events.map(stripName)
			},
			{ fetchOptions: { canRetry: true } }
		)

		return recommendedDatesMapSchema.parse(data)
	} catch (e) {
		trackException(e as Error, 'Failed to load recommended dates of event.', {
			storeName,
			startDate: toISODateStringUTC(startDate) ?? undefined,
			endDate: toISODateStringUTC(endDate) ?? undefined,
			minimalDate: toISODateStringUTC(minimalDate) ?? undefined
		})

		notifyError('Failed to load recommended dates of event.')
	} finally {
		deactivate('recommendedDates')
	}
}

export async function addEvent(storeName: StoreName, uniqueSkuId: string, requestBody: UpsertEventRequestBody) {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		await apiPost(`/events/v2/store/${storeName}/uniqueSkuId/${uniqueSkuId}`, requestBody)
	} catch (e) {
		trackException(e as Error, 'Failed to add the event', { storeName, uniqueSkuId })

		notifyError('Failed to add the event.')
	}
}

export async function updateEvent(eventId: string, requestBody: UpsertEventRequestBody) {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPut } = useApi()

	try {
		await apiPut(`/events/v2/uuid/${eventId}`, requestBody)
	} catch (e) {
		trackException(e as Error, 'Failed to update the event.', { eventId })

		notifyError('Failed to update the event.')
	}
}

export async function removeEvent(eventId: string) {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiDelete } = useApi()

	try {
		await apiDelete(`/events/v2/uuid/${eventId}`)
	} catch (e) {
		trackException(e as Error, 'Failed to delete the event', { eventId })

		notifyError('Failed to delete the event.')
	}
}

export async function fetchProductHistory(storeName: StoreName, uniqueSkuId: string): Promise<HistoryEvent[] | undefined> {
	const loadersStore = useLoadersStore()
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiGet } = useApi()

	try {
		loadersStore.activate('productHistory')

		// in order to show all history, get events since 1990-01-01
		const { data } = await apiGet<HistoryEvent[]>(`/events/v2/store/${storeName}/uniqueSkuId/${uniqueSkuId}/stats`)

		return historyEventsListSchema.parse(data)
	} catch (e) {
		trackException(e as Error, 'Failed to load product history.', { storeName, uniqueSkuId })

		notifyError('Failed to load product history.')
	} finally {
		loadersStore.deactivate('productHistory')
	}
}

export async function fetchDiscountConfig(storeName: StoreName, startDate: DateTime, endDate: DateTime): Promise<DiscountConfig | undefined> {
	const { activate, deactivate } = useLoadersStore()
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		activate('discountConfig')

		const { data } = await apiPost(
			`/events/store/${storeName}/discountConfig`,
			{
				startDate: toISODateStringUTC(startDate),
				endDate: toISODateStringUTC(endDate)
			},
			{ fetchOptions: { canRetry: true } }
		)

		return discountConfigSchema.parse(data)
	} catch (e) {
		trackException(e as Error, 'Failed to load discount config.', {
			storeName,
			startDate: toISODateStringUTC(startDate) ?? undefined,
			endDate: toISODateStringUTC(endDate) ?? undefined
		})

		notifyError('Failed to load discount config.')
	} finally {
		deactivate('discountConfig')
	}
}

async function getOpportunityNearestStartDate(storeName: StoreName) {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { activate, deactivate } = useLoadersStore()
	const { apiGet } = useApi()

	try {
		activate('opportunityStartDate')

		const { data } = await apiGet<Date>(`/events/store/${storeName}/opportunityStartDate`, {
			fetchOptions: { canRetry: true }
		})

		return z
			.string()
			.transform((date) => {
				const asDateTime = DateTime.fromISO(date, { zone: 'UTC' })

				if (asDateTime.isValid) {
					return asDateTime
				}
			})
			.parse(data)
	} catch (e) {
		trackException(e as Error, 'Failed to load opportunity start date.', {
			storeName
		})

		notifyError('Failed to load opportunity start date.')
	} finally {
		deactivate('opportunityStartDate')
	}
}

// We can safely keep the start date per store for 2 minutes
const opportunityNearestStartDateCache = useCache<DateTime<true> | undefined>(2 * 60)

export async function fetchOpportunityNearestStartDate(storeName: StoreName) {
	return await opportunityNearestStartDateCache.get(storeName, () => getOpportunityNearestStartDate(storeName))
}
